import { useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Divider,
  Menu,
  MenuItem,
  Typography,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  getUpcomingReservationsApi,
  cancelReservationApi,
  updateReserationStatusApi,
} from '../../utils/apis';
import ReservationTableSkeleton from './ReservationTableSkeleton';
import { PencilSimple } from '@phosphor-icons/react';
import MessagePopup from '../common/MessagePopup';
import { useAlert } from '../../hooks/useAlert';
import { useUser } from '../../hooks/useUser';
import NoDataFallback from '../common/NoDataFallback';

const UpcomingReservation = ({
  selectedStore,
  isStoresLoading,
}: {
  selectedStore: any;
  isStoresLoading: boolean;
}) => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  // State for menu functionality
  const [copyMenuAnchorEl, setCopyMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [currentBookingId, setCurrentBookingId] = useState<string | null>(null);
  const { alert, setAlert, showAlert } = useAlert();
  const queryClient = useQueryClient();
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const { user } = useUser();

  // Add mutations for both APIs
  const { mutate: updateStatus, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, status }: { id: string; status: number }) =>
      updateReserationStatusApi(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upcoming-reservations'] });
      handleCopyMenuClose();
      showAlert('Reservation updated successfully', 'success');
    },
    onError: error => {
      console.error('Error updating status:', error);
      showAlert('Failed to update reservation status', 'error');
    },
  });

  const { mutate: cancelReservation, isPending: isCancelling } = useMutation({
    mutationFn: (params: {
      tpGuestPhoneNumber: string;
      rgReservationId: string;
      tpReservationId: string;
      cancelReason: string;
    }) => cancelReservationApi(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upcoming-reservations'] });
      setCancelDialogOpen(false);
      setCancelReason('');
      handleCopyMenuClose();
      showAlert('Reservation cancelled successfully', 'success');
    },
    onError: error => {
      console.error('Error cancelling reservation:', error);
      showAlert('Failed to cancel reservation', 'error');
    },
  });

  // Menu handlers
  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    bookingId: string
  ) => {
    setCopyMenuAnchorEl(event.currentTarget);
    setCurrentBookingId(bookingId);
    // Reset selected status when opening menu
    setSelectedStatus(null);
  };

  const handleCopyMenuClose = () => {
    setCopyMenuAnchorEl(null);
    setCurrentBookingId(null);
  };

  const handleStatusSelect = (status: string) => {
    setSelectedStatus(status);
    if (status === 'Cancelled') {
      setCancelDialogOpen(true);
    }
  };

  const handleCancel = () => {
    // Reset selection
    setSelectedStatus(null);
    handleCopyMenuClose();
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setCancelReason('');
    setSelectedStatus(null);
  };

  const handleConfirm = () => {
    if (!selectedStatus || !currentBookingId) return;

    try {
      switch (selectedStatus) {
        case 'Confirmed':
          updateStatus({ id: currentBookingId, status: 3 });
          break;
        case 'Cancelled':
          if (!cancelReason.trim()) {
            showAlert('Please provide a cancellation reason', 'error');
            return;
          }
          cancelReservation({
            tpGuestPhoneNumber: selectedBooking?.tpGuestPhoneNumber,
            rgReservationId: selectedBooking?.rgBookingId,
            tpReservationId: selectedBooking?.rgBookingId,
            cancelReason: cancelReason,
          });
          break;
        case 'Waitlisted':
          updateStatus({ id: currentBookingId, status: 2 });
          break;
        default:
          showAlert('No status selected', 'error');
      }
    } catch (error) {
      console.error('Error updating reservation status:', error);
    }
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'upcoming-reservations',
      paginationModel.page,
      paginationModel.pageSize,
      selectedStore?.id,
    ],
    queryFn: () =>
      getUpcomingReservationsApi({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        restaurantId: selectedStore?.reservation?.restaurantId,
        userId: user?._id,
      }),
    enabled: Boolean(selectedStore?.reservation?.restaurantId),
    gcTime: 1000 * 60 * 10,
    staleTime: 1000 * 60 * 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  if (isLoading || isStoresLoading) {
    return <ReservationTableSkeleton />;
  }

  if (isError && error) {
    return (
      <NoDataFallback
        message="No reservations available"
        description="Looks like there are no reservations to display right now. Once you start receiving reservations, they’ll show up here"
      />
    );
  }

  if (!data || data?.data?.data?.reservations?.length === 0) {
    return (
      <NoDataFallback
        message="No reservations available"
        description="Looks like there are no reservations to display right now. Once you start receiving reservations, they’ll show up here"
      />
    );
  }

  const transformedOrders =
    data?.data?.data?.reservations
      ?.map((booking: any) => {
        // Parse the date strings into Date objects
        const parseDate = (dateStr: string) => {
          if (!dateStr) return null;
          // Split the date string into components
          const [datePart, timePart] = dateStr.split(', ');
          const [day, month, year] = datePart.split('/');
          const [time, period] = timePart.split(' ');
          const [hours, minutes] = time.split(':');

          // Convert to 24-hour format if PM
          let hour = parseInt(hours);
          if (period.toLowerCase() === 'pm' && hour !== 12) {
            hour += 12;
          } else if (period.toLowerCase() === 'am' && hour === 12) {
            hour = 0;
          }

          // Create new Date object (months are 0-based in JavaScript)
          return new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            hour,
            parseInt(minutes)
          );
        };

        // Format the date for display
        const formatDate = (date: Date | null) => {
          if (!date) return '-';

          const dateStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });

          const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });

          return `${dateStr} ${timeStr}`;
        };

        // Parse the dates
        const reservationDate = parseDate(booking.tpReservationTime);
        const createdDate = parseDate(booking.createdAt);

        return {
          rgBookingId: booking.ReservationId || '-',
          tpReservationTime: formatDate(reservationDate),
          tpGuestName: booking.tpGuestName || '-',
          tpGuestCount: booking.tpGuestCount || '-',
          tpGuestPhoneNumber: booking.tpGuestPhoneNumber || '-',
          tpGuestEmail: booking.tpGuestEmail || '-',
          tpReservationStatus: booking.tpReservationStatus || '-',
          tpMessage: booking.tpGuestComments || '-',
          createdAt: formatDate(createdDate),
        };
      })
      ?.sort(
        (a: any, b: any) =>
          new Date(a.tpReservationTime).getTime() -
          new Date(b.tpReservationTime).getTime()
      ) || [];

  const getReservationStatusColor = (status: string) => {
    const normalizedStatus = status.toLowerCase();

    switch (normalizedStatus) {
      case 'pending':
        return 'rgba(244, 197, 66, 0.2)';
      case 'confirmed':
        return 'rgba(55, 183, 125, 0.2)';
      case 'cancelled':
        return 'rgba(231, 76, 60, 0.2)';
      case 'waitlisted':
        return 'rgba(90, 76, 230, 0.1)';
      default:
        return 'rgba(169, 169, 169, 0.2)';
    }
  };

  const getReservationStatusTextColor = (status: string) => {
    const normalizedStatus = status.toLowerCase();

    switch (normalizedStatus) {
      case 'pending':
        return 'rgba(43, 45, 63, 1)';
      case 'confirmed':
        return 'rgba(43, 45, 63, 1)';
      case 'cancelled':
        return 'rgba(43, 45, 63, 1)';
      case 'waitlisted':
        return 'rgba(43, 45, 63, 1)';
      default:
        return 'rgba(43, 45, 63, 1)';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'rgBookingId',
      headerName: 'Booking Id',
      width: 230,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpReservationTime',
      headerName: 'Scheduled For',
      width: 200,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpGuestName',
      headerName: 'Name',
      width: 150,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpGuestCount',
      headerName: 'Party Size',
      width: 120,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpGuestPhoneNumber',
      headerName: 'Phone',
      width: 150,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => `+${params.value}`,
    },
    {
      field: 'tpGuestEmail',
      headerName: 'Email',
      width: 150,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpMessage',
      headerName: 'Message',
      width: 180,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpReservationStatus',
      headerName: 'Status',
      width: 120,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            backgroundColor: getReservationStatusColor(params.value.toString()),
            color: getReservationStatusTextColor(params.value.toString()),
            width: 'auto',
            minWidth: 78,
            maxWidth: 130,
            height: 24,
            borderRadius: '6px',
            padding: '4px 8px',
            fontSize: '12px',
            fontWeight: '400',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created On',
      width: 180,
      headerClassName: 'custom-header',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 120,
      align: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            width: 'auto',
            minWidth: 78,
            maxWidth: 130,
            height: 24,
            borderRadius: '6px',
            padding: '4px 8px',
            fontSize: '12px',
            fontWeight: '400',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
          onClick={e => {
            setSelectedBooking(params.row);
            handleOpenMenu(e, params.row.rgBookingId);
          }}
        >
          <PencilSimple size={16} color="#5A4CE6" />
        </Box>
      ),
    },
  ];

  const handlePaginationModelChange = (newModel: any) => {
    // If page size changed, go back to first page
    if (newModel.pageSize !== paginationModel.pageSize) {
      setPaginationModel({
        page: 0,
        pageSize: newModel.pageSize,
      });
    } else {
      setPaginationModel(newModel);
    }
  };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <DataGrid
        rows={transformedOrders}
        columns={columns}
        getRowId={row => row.rgBookingId}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 20, 50, 75, 100]}
        rowCount={data?.data?.data?.pagination?.totalDocs || 0} // Add this line
        paginationMode="server"
        checkboxSelection={false}
        disableRowSelectionOnClick
        sx={{
          borderRadius: '8px',
          color: 'rgba(107, 111, 128, 1)',
          '& .MuiDataGrid-root': {
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-main': {
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
          },
          '& .MuiDataGrid-columnHeader': {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
          },
          '& .custom-header': {
            bgcolor: '#F8F9FC',
            color: 'custom.textBlue',
            fontSize: '14px',
            fontWeight: 600,
            padding: '12px 16px',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: 'rgba(43, 45, 63, 1)',
            fontWeight: 600,
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: '#E7F0FF !important',
          },
          '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
        }}
      />

      {/* Status Menu */}
      <Menu
        anchorEl={copyMenuAnchorEl}
        open={Boolean(copyMenuAnchorEl)}
        onClose={handleCopyMenuClose}
        slotProps={{
          paper: {
            sx: {
              width: '175px',
              mt: 1,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              border: '1px solid #E0E0E0',
            },
          },
        }}
      >
        <Typography
          sx={{
            p: '8px 12px',
            fontSize: '12px',
            fontWeight: 500,
            color: '#2B2D3F',
          }}
        >
          Modify Reservation Status
        </Typography>
        <Divider sx={{ my: 0.5 }} />

        {['Cancelled', 'Waitlisted', 'Confirmed'].map(status => (
          <MenuItem
            key={status}
            dense
            onClick={() => handleStatusSelect(status)}
            sx={{
              '&.Mui-disabled': {
                opacity: 0.5,
              },
              justifyContent: 'flex-start', // Align items to the left
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedStatus === status}
                  size="small"
                  sx={{
                    color: '#6B6F80',
                    borderRadius: '6px', // Border radius for the checkbox
                    '& .MuiSvgIcon-root': {
                      borderRadius: '6px', // Ensures the checkbox shape
                    },
                    '&.Mui-checked': {
                      color: 'primary.main', // Checkbox color when checked
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(107, 111, 128, 0.1)', // Light hover effect
                    },
                    '&.Mui-focusVisible': {
                      outline: '2px solid #6B6F80', // Outline when focused
                      borderRadius: '6px',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '14px', color: '#6B6F80' }}>
                  {status}
                </Typography>
              }
              sx={{ width: '100%', m: 0, justifyContent: 'flex-start' }}
            />
          </MenuItem>
        ))}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 1,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          <Button
            size="small"
            sx={{
              fontSize: '12px',
              textTransform: 'none',
              color: 'text.secondary',
              mr: 1,
            }}
            onClick={handleCancel}
            disabled={isUpdating || isCancelling}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{
              fontSize: '12px',
              textTransform: 'none',
              boxShadow: 'none',
              borderRadius: '6px',
              px: 2,
              minWidth: '80px',
            }}
            onClick={handleConfirm}
            disabled={!selectedStatus || isUpdating || isCancelling}
          >
            {isUpdating || isCancelling ? 'Updating...' : 'Confirm'}
          </Button>
        </Box>
      </Menu>

      {/* Add Cancel Reason Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        PaperProps={{
          sx: {
            width: '400px',
            borderRadius: '8px',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            color: '#2B2D3F',
            pb: 1,
          }}
        >
          Cancellation Reason
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder="Please provide a reason for cancellation"
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleCancelDialogClose}
            disabled={isCancelling}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontSize: '14px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!cancelReason.trim() || isCancelling}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              boxShadow: 'none',
              borderRadius: '6px',
              px: 3,
              minWidth: '100px',
            }}
          >
            {isCancelling ? 'Cancelling...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default UpcomingReservation;
